import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { signIn, signOut, confirmSignIn, getCurrentUser } from 'aws-amplify/auth';
import { validatePassword, normalizeTeacherId } from './authUtils';

// Security configuration
const SECURITY_CONFIG = {
  MAX_LOGIN_ATTEMPTS: 5,
  LOCKOUT_DURATION: 15 * 60 * 1000, // 15 minutes
  SESSION_TIMEOUT: 30 * 60 * 1000, // 30 minutes
  PASSWORD_VISIBILITY_TIMEOUT: 30000 // 30 seconds
};

function Login() {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmNewPassword, setConfirmNewPassword] = useState("");
  const [error, setError] = useState("");
  const [showPasswordReset, setShowPasswordReset] = useState(false);
  const [pendingUser, setPendingUser] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [loginAttempts, setLoginAttempts] = useState(0);
  const [isLocked, setIsLocked] = useState(false);
  const [lockoutTimer, setLockoutTimer] = useState(null);
  const [passwordStrength, setPasswordStrength] = useState("");
  
  const navigate = useNavigate();
  const sessionTimeoutRef = useRef(null);

  // Check for existing lockout on component mount
  useEffect(() => {
    const lockoutData = sessionStorage.getItem('loginLockout');
    if (lockoutData) {
      const { timestamp, attempts } = JSON.parse(lockoutData);
      const timePassed = Date.now() - timestamp;
      
      if (timePassed < SECURITY_CONFIG.LOCKOUT_DURATION && attempts >= SECURITY_CONFIG.MAX_LOGIN_ATTEMPTS) {
        setIsLocked(true);
        setLoginAttempts(attempts);
        const remainingTime = SECURITY_CONFIG.LOCKOUT_DURATION - timePassed;
        setLockoutTimer(Math.ceil(remainingTime / 60000)); // Convert to minutes
        
        const timer = setInterval(() => {
          const newRemainingTime = SECURITY_CONFIG.LOCKOUT_DURATION - (Date.now() - timestamp);
          if (newRemainingTime <= 0) {
            clearInterval(timer);
            setIsLocked(false);
            setLoginAttempts(0);
            setLockoutTimer(null);
            sessionStorage.removeItem('loginLockout');
          } else {
            setLockoutTimer(Math.ceil(newRemainingTime / 60000));
          }
        }, 60000);
      } else if (timePassed >= SECURITY_CONFIG.LOCKOUT_DURATION) {
        // Lockout expired, clear it
        sessionStorage.removeItem('loginLockout');
      } else {
        setLoginAttempts(attempts);
      }
    }
  }, []);

  // Session timeout management
  const setupSessionTimeout = () => {
    if (sessionTimeoutRef.current) {
      clearTimeout(sessionTimeoutRef.current);
    }
    
    sessionTimeoutRef.current = setTimeout(() => {
      handleSessionTimeout();
    }, SECURITY_CONFIG.SESSION_TIMEOUT);
  };

  const handleSessionTimeout = async () => {
    try {
      await signOut();
      sessionStorage.clear();
      setError("Session expired for security. Please log in again.");
      navigate("/login");
    } catch (error) {
      // Silently handle signout errors
      console.warn("Session cleanup attempted");
    }
  };

  // Rate limiting for login attempts
  const updateLoginAttempts = (failed = true) => {
    const newAttempts = failed ? loginAttempts + 1 : 0;
    setLoginAttempts(newAttempts);
    
    const lockoutData = {
      timestamp: Date.now(),
      attempts: newAttempts
    };
    
    sessionStorage.setItem('loginLockout', JSON.stringify(lockoutData));
    
    if (newAttempts >= SECURITY_CONFIG.MAX_LOGIN_ATTEMPTS) {
      setIsLocked(true);
      setLockoutTimer(Math.ceil(SECURITY_CONFIG.LOCKOUT_DURATION / 60000));
      setError(`Too many failed attempts. Account locked for ${Math.ceil(SECURITY_CONFIG.LOCKOUT_DURATION / 60000)} minutes.`);
    }
  };

  // Password strength indicator
  const checkPasswordStrength = (pwd) => {
    if (!pwd) {
      setPasswordStrength("");
      return;
    }
    
    let strength = 0;
    const checks = [
      /[a-z]/.test(pwd), // lowercase
      /[A-Z]/.test(pwd), // uppercase
      /[0-9]/.test(pwd), // numbers
      /[^A-Za-z0-9]/.test(pwd), // special chars
      pwd.length >= 8, // length
      pwd.length >= 12 // good length
    ];
    
    strength = checks.filter(Boolean).length;
    
    if (strength < 3) setPasswordStrength("Weak");
    else if (strength < 5) setPasswordStrength("Medium");
    else setPasswordStrength("Strong");
  };

  // Sanitized error logging
  const logSecureError = (error, context = "") => {
    const sanitizedError = {
      name: error.name,
      type: error.constructor.name,
      context: context,
      timestamp: new Date().toISOString()
    };
    
    // Only log error name and type, never full error details
    console.error("Authentication error:", sanitizedError);
    
    // In production, send to secure logging service
    if (process.env.NODE_ENV === 'production') {
      // TODO: Send to secure audit logging service
      // sendToAuditLog(sanitizedError);
    }
  };

  async function login() {
    if (isLocked) {
      setError(`Account temporarily locked. Try again in ${lockoutTimer} minutes.`);
      return;
    }

    if (!username || !password) {
      setError("Please enter both username and password.");
      return;
    }
    
    setError("");
    setIsLoading(true);

    try {
      // Force sign out any existing session first
      try {
        await signOut();
      } catch (signOutError) {
        console.warn("Session cleanup attempted");
      }

      // Attempt authentication
      const result = await signIn({ 
        username: username.trim(), 
        password: password 
      });
      
      // Log successful authentication attempt (no sensitive data)
      console.info("Authentication completed", {
        user: username.trim(),
        timestamp: new Date().toISOString(),
        userAgent: navigator.userAgent.substring(0, 100) // Truncated user agent
      });

      // Check if user needs to change password
      if (result.nextStep?.signInStep === 'CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED') {
        console.info("Password change required for user");
        setShowPasswordReset(true);
        setPendingUser(result);
        setError("Please set a new password to continue.");
        setIsLoading(false);
        return;
      }

      // Normal login complete
      if (result.isSignedIn) {
        updateLoginAttempts(false); // Reset attempts on success
        completeLogin(username.trim());
        setIsLoading(false);
      }

    } catch (err) {
      logSecureError(err, "login_attempt");
      updateLoginAttempts(true); // Increment failed attempts
      
      // Provide user-friendly error messages without exposing system details
      let errorMessage = "Authentication failed. Please check your credentials.";
      
      if (err.name === 'NotAuthorizedException') {
        errorMessage = "Incorrect username or password.";
      } else if (err.name === 'UserNotFoundException') {
        errorMessage = "User not found. Please check your username.";
      } else if (err.name === 'UserNotConfirmedException') {
        errorMessage = "Account not confirmed. Please check your email for confirmation instructions.";
      } else if (err.name === 'TooManyRequestsException') {
        errorMessage = "Too many requests. Please wait before trying again.";
      } else if (err.name === 'PasswordResetRequiredException') {
        errorMessage = "Password reset required. Please contact support.";
      } else if (err.name === 'UserLambdaValidationException') {
        errorMessage = "Account validation failed. Please contact support.";
      }
      
      setError(errorMessage);
      setIsLoading(false);
      
      // Clear password field on failed attempt
      setPassword("");
    }
  }

  async function handlePasswordReset() {
    if (!newPassword || !confirmNewPassword) {
      setError("Please enter both new password fields.");
      return;
    }

    if (newPassword !== confirmNewPassword) {
      setError("New passwords do not match.");
      return;
    }

    // Enhanced password validation
    const passwordValidation = validatePassword(newPassword);
    if (passwordValidation) {
      setError(passwordValidation);
      return;
    }

    setIsLoading(true);

    try {
      const result = await confirmSignIn({ 
        challengeResponse: newPassword
      });

      console.info("Password reset completed successfully");

      if (result.isSignedIn) {
        completeLogin(username.trim());
        setIsLoading(false);
      }

    } catch (err) {
      logSecureError(err, "password_reset");
      
      let errorMessage = "Failed to reset password. Please try again.";
      
      if (err.name === 'InvalidPasswordException') {
        errorMessage = "Password does not meet security requirements.";
      } else if (err.name === 'LimitExceededException') {
        errorMessage = "Too many attempts. Please try again later.";
      } else if (err.name === 'InvalidParameterException') {
        errorMessage = "Invalid password format. Please check requirements.";
      }
      
      setError(errorMessage);
      setIsLoading(false);
    }
  }

  function completeLogin(username) {
    try {
      // Normalize teacherId (remove trailing '01' if exists)
      const normalizedTeacherId = normalizeTeacherId(username);

      // Create secure session data
      const sessionData = {
        teacherId: normalizedTeacherId,
        loginTime: new Date().toISOString(),
        sessionId: `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`
      };

      // Store session data
      sessionStorage.setItem('currentTeacher', normalizedTeacherId);
      sessionStorage.setItem('sessionData', JSON.stringify(sessionData));
      
      // Setup session timeout
      setupSessionTimeout();

      console.info("Login completed successfully", {
        teacherId: normalizedTeacherId,
        timestamp: sessionData.loginTime
      });

      // Navigate to dashboard
      navigate("/dashboard", { state: { teacherId: normalizedTeacherId } });
      
    } catch (error) {
      logSecureError(error, "session_creation");
      setError("Session creation failed. Please try logging in again.");
    }
  }

  // Handle new password input for strength checking
  const handleNewPasswordChange = (e) => {
    const pwd = e.target.value;
    setNewPassword(pwd);
    checkPasswordStrength(pwd);
  };

  // Cleanup on component unmount
  useEffect(() => {
    return () => {
      if (sessionTimeoutRef.current) {
        clearTimeout(sessionTimeoutRef.current);
      }
    };
  }, []);

  // Render password reset form
  if (showPasswordReset) {
    return (
      <div id="login-screen">
        <div className="login-box">
          <h2>Set New Password</h2>
          <p>Please set a secure password to continue.</p>
          
          <input
            type="password"
            placeholder="New Password"
            value={newPassword}
            onChange={handleNewPasswordChange}
            autoComplete="new-password"
            disabled={isLoading}
          />
          {passwordStrength && (
            <div className={`password-strength password-${passwordStrength.toLowerCase()}`}>
              Password Strength: {passwordStrength}
            </div>
          )}
          
          <input
            type="password"
            placeholder="Confirm New Password"
            value={confirmNewPassword}
            onChange={(e) => setConfirmNewPassword(e.target.value)}
            autoComplete="new-password"
            disabled={isLoading}
          />
          
          <button 
            onClick={handlePasswordReset}
            disabled={isLoading || !newPassword || !confirmNewPassword}
            style={{ 
              opacity: isLoading ? 0.6 : 1, 
              cursor: isLoading ? 'not-allowed' : 'pointer' 
            }}
          >
            {isLoading ? 'Setting Password...' : 'Set New Password'}
          </button>
          {error && <p style={{ color: "red" }}>{error}</p>}
        </div>
      </div>
    );
  }

  // Render normal login form
  return (
    <div id="login-screen">
      <div className="login-box">
        <h2>Welcome to Individualized Education</h2>
        
        <input
          type="text"
          placeholder="Username"
          value={username}
          onChange={(e) => setUsername(e.target.value)}
          autoComplete="username"
          disabled={isLoading || isLocked}
          onKeyPress={(e) => e.key === 'Enter' && !isLocked && login()}
        />
        
        <input
          type="password"
          placeholder="Password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          autoComplete="current-password"
          disabled={isLoading || isLocked}
          onKeyPress={(e) => e.key === 'Enter' && !isLocked && login()}
        />
        
        {loginAttempts > 0 && loginAttempts < SECURITY_CONFIG.MAX_LOGIN_ATTEMPTS && (
          <p style={{ color: "orange", fontSize: "0.9em" }}>
            Warning: {loginAttempts} failed attempt(s). {SECURITY_CONFIG.MAX_LOGIN_ATTEMPTS - loginAttempts} remaining.
          </p>
        )}
        
        <button 
          onClick={login}
          disabled={isLoading || isLocked}
          style={{ 
            opacity: isLoading || isLocked ? 0.6 : 1, 
            cursor: isLoading || isLocked ? 'not-allowed' : 'pointer' 
          }}
        >
          {isLoading ? 'Signing In...' : isLocked ? `Locked (${lockoutTimer}m)` : 'Login'}
        </button>
        
        {error && <p style={{ color: "red" }}>{error}</p>}
      </div>
    </div>
  );
}

export default Login;
